import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { useAuth } from '../context/AuthContext';

const MessageHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recipientTypeFilter, setRecipientTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalMessages, setTotalMessages] = useState(0);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [broadcastFilter, setBroadcastFilter] = useState(null);
  const [broadcastName, setBroadcastName] = useState('');

  // Load messages on component mount
  useEffect(() => {
    loadMessages();
  }, [page, rowsPerPage, statusFilter, recipientTypeFilter]);

  // Check for broadcast filter from BroadcastCenter
  useEffect(() => {
    const broadcastFilter = sessionStorage.getItem('filterByBroadcast');
    if (broadcastFilter) {
      // Clear the filter from storage
      sessionStorage.removeItem('filterByBroadcast');
      // Set the broadcast filter and clear search term
      setBroadcastFilter(broadcastFilter);
      setSearchTerm(''); // Clear search term when filtering by broadcast
      // Load messages with broadcast filter
      setPage(0);
      // Fetch broadcast name
      fetchBroadcastName(broadcastFilter);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        setPage(0);
        loadMessages();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reload messages when broadcast filter changes
  useEffect(() => {
    if (broadcastFilter) {
      loadMessages();
    }
  }, [broadcastFilter]);

  const fetchBroadcastName = async (broadcastId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/broadcasts/${broadcastId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const broadcast = await response.json();
        setBroadcastName(broadcast.name);
      }
    } catch (error) {
      console.error('Error fetching broadcast name:', error);
      setBroadcastName(`Broadcast ${broadcastId}`);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (recipientTypeFilter !== 'all') {
        params.append('recipient_type', recipientTypeFilter);
      }

      // Add broadcast ID filter if set
      if (broadcastFilter) {
        params.append('broadcast_id', broadcastFilter);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/messages?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.data || []);
        setTotalMessages(data.total || 0);
      } else {
        setError('Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setPage(0);
    loadMessages();
  };

  const handleSearch = () => {
    setPage(0);
    loadMessages();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
    loadMessages();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'queued': 'warning',
      'sent': 'info',
      'delivered': 'success',
      'failed': 'error',
      'undelivered': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'queued': <PendingIcon />,
      'sent': <ScheduleIcon />,
      'delivered': <CheckCircleIcon />,
      'failed': <ErrorIcon />,
      'undelivered': <ErrorIcon />
    };
    return icons[status] || <PendingIcon />;
  };

  const getStatusText = (status) => {
    const statusMap = {
      'queued': 'Queued',
      'sent': 'Sent',
      'delivered': 'Delivered',
      'failed': 'Failed',
      'undelivered': 'Undelivered'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    // Format phone number for display
    return phone.replace(/^\+1/, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const getRecipientDisplayName = (message) => {
    if (message.recipient_first_name && message.recipient_last_name) {
      return `${message.recipient_first_name} ${message.recipient_last_name}`;
    } else if (message.recipient_first_name) {
      return message.recipient_first_name;
    }
    return 'Unknown Recipient';
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setShowMessageDialog(true);
  };

  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#DC2626', fontWeight: 600 }}>
          <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          SMS Message History
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {broadcastFilter && (
            <Chip 
              label={`Viewing: ${broadcastName || `Broadcast ${broadcastFilter}`}`}
              color="primary"
              variant="outlined"
              onDelete={() => {
                setBroadcastFilter(null);
                setBroadcastName('');
                setSearchTerm('');
                // Reload messages to show all messages again
                setPage(0);
                loadMessages();
              }}
            />
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderColor: '#DC2626', color: '#DC2626' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Broadcast Context Note */}
      {broadcastFilter && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            You're viewing the Message History tab after viewing {broadcastName || `Broadcast ${broadcastFilter}`}. 
            Messages from this broadcast are filtered below. 
            Click the X on the broadcast chip above to clear this filter and view all messages.
          </Alert>
        </Grid>
      )}

      {/* Filters and Search */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
                      <TextField
              fullWidth
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm.trim() && (
                      <Button 
                        size="small" 
                        onClick={handleClearSearch}
                        sx={{ color: '#6b7280', mr: 1 }}
                      >
                        Clear
                      </Button>
                    )}
                    <Button 
                      size="small" 
                      onClick={handleSearch}
                      sx={{ color: '#DC2626' }}
                    >
                      Search
                    </Button>
                  </InputAdornment>
                )
              }}
            />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="queued">Queued</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="undelivered">Undelivered</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Recipient Type</InputLabel>
            <Select
              value={recipientTypeFilter}
              onChange={(e) => setRecipientTypeFilter(e.target.value)}
              label="Recipient Type"
            >
              <MenuItem value="all">All Types</MenuItem>
                              <MenuItem value="first_timer">First Timers</MenuItem>
                <MenuItem value="new_convert">New Converts</MenuItem>
                <MenuItem value="harvest">Harvest Contacts</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={2}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
            Total: {totalMessages}
          </Typography>
        </Grid>
      </Grid>

      {/* Messages Display */}
      {isMobile ? (
        // Mobile Cards View
        <Grid container spacing={2}>
          {messages.map((message) => (
            <Grid item xs={12} key={message.id}>
              <Card sx={{ 
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
              }}>
                <CardContent>
                  {/* Header with Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                        {getRecipientDisplayName(message)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        {formatPhoneNumber(message.recipient_phone_number)}
                      </Typography>
                    </Box>
                    <Chip 
                      icon={getStatusIcon(message.status)}
                      label={getStatusText(message.status)}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </Box>
                  
                  {/* Message Content */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                      <MessageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Message:
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      background: '#f9fafb', 
                      p: 1.5, 
                      borderRadius: 1,
                      border: '1px solid #e5e7eb'
                    }}>
                      {message.message_text}
                    </Typography>
                  </Box>
                  
                  {/* Message Details */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Sent: {formatDate(message.sent_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        <PersonIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Type: {message.recipient_type}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Twilio SID */}
                  {message.twilio_sid && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      SID: {message.twilio_sid}
                    </Typography>
                  )}
                  
                  {/* Error Details */}
                  {message.error_details && (
                    <Box sx={{ mt: 1, p: 1, background: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                      <Typography variant="caption" color="error.main" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Error Details:
                      </Typography>
                      <Typography variant="caption" color="error.main" sx={{ fontSize: '0.75rem' }}>
                        {message.error_details}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton 
                    color="primary" 
                    onClick={() => handleViewMessage(message)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} sx={{ background: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Broadcast</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Error Details</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.id} sx={{ '&:hover': { background: '#f9fafb' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getRecipientDisplayName(message)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {formatPhoneNumber(message.recipient_phone_number)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                    <Typography variant="body2" sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {message.message_text}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={getStatusIcon(message.status)}
                      label={getStatusText(message.status)}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {formatDate(message.sent_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={message.recipient_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {message.broadcast_name ? (
                      <Chip 
                        label={message.broadcast_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Individual
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {message.error_details ? (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'error.main',
                          fontSize: '0.75rem',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          title: message.error_details
                        }}
                      >
                        {message.error_details}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleViewMessage(message)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalMessages}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* No Messages State */}
      {!loading && messages.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <MessageIcon sx={{ fontSize: 64, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No messages found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || statusFilter !== 'all' || recipientTypeFilter !== 'all' 
              ? 'Try adjusting your filters or search terms'
              : 'Start sending SMS messages to see them here'
            }
          </Typography>
        </Box>
      )}

      {/* Message Detail Dialog */}
      <Dialog 
        open={showMessageDialog} 
        onClose={() => setShowMessageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon sx={{ color: '#DC2626' }} />
            Message Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Recipient:</strong> {getRecipientDisplayName(selectedMessage)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {formatPhoneNumber(selectedMessage.recipient_phone_number)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Status:</strong> 
                      <Chip 
                        icon={getStatusIcon(selectedMessage.status)}
                        label={getStatusText(selectedMessage.status)}
                        color={getStatusColor(selectedMessage.status)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Type:</strong> {selectedMessage.recipient_type}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Sent:</strong> {formatDate(selectedMessage.sent_at)}
                    </Typography>
                  </Grid>
                  {selectedMessage.delivered_at && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Delivered:</strong> {formatDate(selectedMessage.delivered_at)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Message Content */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                  Message Content
                </Typography>
                <Paper sx={{ p: 2, background: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.message_text}
                  </Typography>
                </Paper>
              </Grid>

              {/* Template Information */}
              {selectedMessage.template_name && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                    Template Used
                  </Typography>
                  <Chip 
                    label={selectedMessage.template_name}
                    variant="outlined"
                    color="primary"
                  />
                </Grid>
              )}

              {/* Twilio Information */}
              {selectedMessage.twilio_sid && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#374151' }}>
                    Twilio Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>SID:</strong> {selectedMessage.twilio_sid}
                  </Typography>
                </Grid>
              )}

              {/* Error Details */}
              {selectedMessage.error_details && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#DC2626' }}>
                    Error Details
                  </Typography>
                  <Paper sx={{ p: 2, background: '#fef2f2', border: '1px solid #fecaca' }}>
                    <Typography variant="body2" color="error.main" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedMessage.error_details}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMessageDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MessageHistory;
